import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { Clock, AlertCircle, MessageCircle } from 'lucide-react';

const SUPPORT_WHATSAPP = import.meta.env.VITE_SUPPORT_WHATSAPP;

export default function PaymentPending() {
  const [searchParams] = useSearchParams();
  const paymentId = searchParams.get('payment_id');
  const status = searchParams.get('status');

  useEffect(() => {
    // Log para analytics/tracking
    console.log('Payment Pending:', { paymentId, status });

    // Limpar o link do Mercado Pago do localStorage quando pendente
    // (não permitir retry direto, pois o pagamento está em processamento)
    localStorage.removeItem('mercadopago_init_point');
  }, [paymentId, status]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF7F2] to-[#DED1BD]/30 dark:from-[#0f0606] dark:to-[#240404] flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center pb-6">
          <div className="mx-auto mb-4 w-20 h-20 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center">
            <Clock className="h-12 w-12 text-yellow-600 dark:text-yellow-400" />
          </div>
          <CardTitle className="text-2xl font-bold text-yellow-900 dark:text-yellow-100">
            Pagamento Pendente
          </CardTitle>
          <CardDescription className="text-base mt-2">
            Estamos processando seu pagamento
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {paymentId && (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">ID do Pagamento</p>
              <p className="text-sm font-mono font-medium text-gray-900 dark:text-gray-100">
                {paymentId}
              </p>
            </div>
          )}

          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-900 dark:text-yellow-100 mb-1">
                  Aguardando Confirmação
                </p>
                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                  Seu pagamento está sendo processado. Isso pode levar alguns instantes. Você será
                  notificado assim que o pagamento for confirmado.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-[#DED1BD]/30 dark:bg-[#B05D41]/10 border border-[#B05D41]/30 dark:border-[#B05D41]/30 rounded-lg p-4">
            <p className="text-sm text-[#240404] dark:text-[#DED1BD] mb-2 font-medium">
              O que acontece agora?
            </p>
            <ul className="text-sm text-[#240404]/80 dark:text-[#DED1BD]/80 space-y-2 list-disc list-inside">
              <li>Aguarde a confirmação do pagamento</li>
              <li>Você receberá um e-mail com a atualização</li>
              <li>O processo pode levar até 48 horas úteis</li>
            </ul>
          </div>

          <div className="pt-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
            <p className="text-xs text-center text-gray-600 dark:text-gray-400">
              Em caso de dúvidas, entre em contato conosco
            </p>
            {SUPPORT_WHATSAPP && (
              <Button
                onClick={() => {
                  const phone = SUPPORT_WHATSAPP.replace(/\D/g, '');
                  const message = encodeURIComponent(
                    `Olá! Meu pagamento está pendente (ID: ${paymentId || 'não informado'}) e gostaria de saber mais informações sobre o status.`
                  );
                  window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
                }}
                variant="outline"
                className="w-full"
                size="sm"
              >
                <MessageCircle className="mr-2 h-4 w-4" />
                Falar com Suporte
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
